import { formatINR } from "@/utils/formatINR";
import { AlertTriangle } from "lucide-react";
import ScreenshotThumb from "@/components/ScreenshotThumb";

export default function AssemblyLiveCalcPanel({ calc, threshold = 15, hideMargin = false, entityId, screenshotPath }) {
  const grandTotal = calc?.grand_total || 0;
  const margin = calc?.margin_amount || 0;
  const marginPct = calc?.margin_pct || 0;
  const redFlag = grandTotal > 0 && marginPct < threshold;
  // Each cost line's share of the Grand Total, for the % breakdown pills.
  const pctOf = (v) => (grandTotal > 0 && v != null ? (Number(v) / grandTotal) * 100 : null);

  // Part photo (drawing screenshot) — shown alongside the live cost, same as the costing form.
  const screenshotBlock = (screenshotPath && entityId) ? (
    <div className="mt-4 pt-4 border-t border-slate-200" data-testid="assembly-live-calc-screenshot">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Part photo</div>
      <div className="border border-slate-200 rounded-md bg-slate-50 overflow-hidden h-40 flex items-center justify-center">
        <ScreenshotThumb scope="assembly" entityId={entityId} path={screenshotPath} className="w-full h-full flex items-center justify-center" />
      </div>
    </div>
  ) : null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 lg:sticky lg:top-4" data-testid="assembly-live-calc">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm text-slate-900">Live Calculation</h3>
        <div className="text-[10px] uppercase tracking-wider text-orange-600 bg-orange-50 px-2 py-0.5 rounded">auto</div>
      </div>
      <div className="space-y-0.5 text-sm">
        <Row label="Components Total" value={calc?.components_total} bold section target="asm-components" pct={pctOf(calc?.components_total)} />
        <Row label="B'. Assembly Process" value={calc?.assembly_process_cost} bold section target="asm-process" pct={pctOf(calc?.assembly_process_cost)} />
        <Row label="C'. Assembly BOP" value={calc?.assembly_bop_cost} bold section target="asm-bop" pct={pctOf(calc?.assembly_bop_cost)} />
        <Row label="D'. Assembly Treatment" value={calc?.assembly_treatment_total} bold section target="asm-treatment" pct={pctOf(calc?.assembly_treatment_total)} />
        <Row label="Sub-Total" value={calc?.sub_total} highlight pct={pctOf(calc?.sub_total)} />
        <Row label="E'. Overheads & Margin" value={calc?.overheads_and_margin} bold section target="asm-overheads" pct={pctOf(calc?.overheads_and_margin)} />
        <div className="mt-2 pt-2 border-t-2 border-orange-500">
          <Row label="Grand Total / Selling Price" value={grandTotal} grandTotal pct={grandTotal > 0 ? 100 : null} />
        </div>
        {(calc?.development_cost_total || 0) > 0 && (
          <div className="mt-2 pt-2 border-t border-dashed border-slate-300">
            <Row label="Development Cost (one-time)" value={calc?.development_cost_total} bold />
            <div className="text-[10px] text-slate-400 leading-tight">Charged once — not part of the per-piece Grand Total above.</div>
          </div>
        )}
      </div>

      {!hideMargin && (
        <div className="mt-4 pt-4 border-t border-slate-200 space-y-1">
          <div className="flex justify-between">
            <span className={`text-sm ${redFlag ? "text-red-600" : "text-slate-700"}`}>Margin</span>
            <span className={`font-mono text-sm font-semibold ${redFlag ? "text-red-600" : "text-emerald-600"}`}>
              {formatINR(margin)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={`text-sm ${redFlag ? "text-red-600" : "text-slate-700"}`}>Margin %</span>
            <span className={`font-mono text-sm font-bold ${redFlag ? "text-red-600" : "text-emerald-600"}`}>
              {grandTotal > 0 ? marginPct.toFixed(2) + "%" : "—"}
            </span>
          </div>
          {redFlag && (
            <div className="mt-2 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded">
              <AlertTriangle size={13} /> Below {threshold}% margin threshold
            </div>
          )}
        </div>
      )}

      {screenshotBlock}
      <SourcingRollup calc={calc} />
    </div>
  );
}

function scrollToSection(id) {
  const el = typeof document !== "undefined" && document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.classList.add("ring-2", "ring-orange-400");
  setTimeout(() => el.classList.remove("ring-2", "ring-orange-400"), 1200);
}

function SourcingRollup({ calc }) {
  const unavailable = !!calc?.sourcing_rollup_unavailable;
  const groups = calc?.monthly_rm_by_material || [];
  const moqPcs = calc?.customer_moq_pcs;
  const moqLb = !!calc?.customer_moq_is_lower_bound;
  const moqDriver = calc?.customer_moq_driver;
  const hasAnything = unavailable || groups.length > 0 || moqPcs != null;
  if (!hasAnything) return null;

  return (
    <div className="mt-4 pt-4 border-t border-slate-200 space-y-3" data-testid="assembly-sourcing-rollup">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sourcing</div>

      {moqPcs != null && (
        <div
          className="flex items-baseline justify-between bg-slate-50 px-2 py-1.5 rounded"
          data-testid="assembly-customer-moq-pill"
        >
          <span className="text-sm text-slate-700">Customer MOQ</span>
          <span className="font-mono text-sm font-bold text-slate-900">
            {moqLb ? "≥ " : ""}{Number(moqPcs).toLocaleString()} assemblies
          </span>
        </div>
      )}
      {moqDriver && (
        <div className="text-[11px] text-slate-500 leading-snug" data-testid="assembly-moq-driver">
          Driven by <span className="font-mono">{moqDriver.npd_number || moqDriver.npd_id}</span> — {moqDriver.reason}
        </div>
      )}

      {unavailable ? (
        <div
          className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1.5 rounded"
          data-testid="assembly-sourcing-unavailable"
        >
          Sourcing rollup unavailable — add peak-year monthly volume to compute material requirements.
        </div>
      ) : (
        groups.map((g) => (
          <div key={`${g.material_id}||${g.material_grade}`} className="text-xs" data-testid={`assembly-mat-row-${g.material_id}`}>
            <div className="flex items-baseline justify-between">
              <span className="text-slate-700">{g.material_label}</span>
              <span className={`font-mono font-semibold ${g.low_volume_flag ? "text-red-600" : "text-slate-900"}`}>
                {Number(g.monthly_rm_kg).toLocaleString()} kg/mo
              </span>
            </div>
            {g.thickness_label && (
              <div className="text-[10px] text-slate-500">Thickness: {g.thickness_label} mm</div>
            )}
            <div className="text-[10px] text-slate-500">
              threshold {Number(g.threshold_kg).toLocaleString()} kg/mo
              {g.low_volume_flag && (
                <span className="ml-1 text-red-600 font-semibold" data-testid={`assembly-low-vol-${g.material_id}`}>
                  ⚠ low volume
                </span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Row({ label, value, bold, highlight, section, grandTotal, target, pct }) {
  const clickable = !!target;
  const cls = [
    "flex justify-between items-baseline py-0.5",
    highlight ? "bg-slate-50 -mx-2 px-2 py-1 rounded" : "",
    section ? "border-t border-slate-100 pt-1 mt-0.5" : "",
    clickable ? "cursor-pointer hover:bg-orange-50 -mx-2 px-2 rounded transition-colors" : "",
  ].join(" ");
  const labelCls = `text-sm ${bold ? "font-medium" : ""} ${grandTotal ? "font-bold text-slate-900" : "text-slate-700"} ${clickable ? "hover:text-orange-700" : ""}`;
  const valCls = `font-mono text-sm ${bold ? "font-semibold" : ""} text-slate-900 ${grandTotal ? "text-base font-bold text-orange-600" : ""}`;
  return (
    <div
      className={cls}
      onClick={clickable ? () => scrollToSection(target) : undefined}
      title={clickable ? "Go to this section" : undefined}
      data-testid={target ? `asm-calc-jump-${target}` : undefined}
    >
      <span className={labelCls}>{label}</span>
      <span className="flex items-baseline gap-2">
        {pct != null && (
          <span
            className={`font-mono text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded ${grandTotal ? "bg-orange-100 text-orange-700" : "bg-indigo-50 text-indigo-600"}`}
            title="Share of Grand Total"
          >
            {pct.toFixed(1)}%
          </span>
        )}
        <span className={valCls}>{formatINR(value)}</span>
      </span>
    </div>
  );
}
